import { gql } from '@apollo/client';
import { Company, CompanySettings, DeleteCompanyInput, UpdateCompanyInput } from '../types/company';
import { forPaginated } from './common/paginated.graphql';

const company = gql`
  fragment Company on Company {
    id
    version
    companyId
    name
    partner
    settings {
      primaryColor
      accentColor
      headerColor
      companyLogo
      companyFavicon
      language
      scanConfigurations {
        regex
        barcodeFormat
        regexOption
        type
        typeId
      }
      featureToggles {
        alerts {
          alerts
        }
        assets {
          assets
          inboundOrder
        }
        orders {
          orders
          externalReferenceId
          purchaseOrderNumber
          estimatedTimeOfArrival
          customer
          supplier
          quickOrder
          editProductsOnInbound
          editProductsOnOutbound
          editProductsOnMove
          editProductsOnDisposal
          productSelectionFirst
          sendMailOnAssigned
          ignoreAvailableQuantity
          picking
          productBasedApproach
          processCompletedOrders
        }
        products {
          container
          gs1Barcodes
          products
          displayEmptyStock
        }
        productTransactions {
          productTransactions
          web
          mobile
          quickActions
        }
        company {
          ventoryVisionAR
          sessionStorage
        }
        sap {
          allowUncounted
        }
        tasks {
          tasks
          allowStockUpdate
          cycleCount
          blindCount
          quickCount
          taskOrders {
            taskOrders
            inbound
            move
            outbound
            auto
            refill {
              refill
              ignoreOverstock
            }
          }
        }
        containers {
          containers
          allowBinMismatch
        }
        lots {
          lots
          lotUniqueCompany
          lotUniqueProductMasterData
          expiryManagement
        }
        scan {
          handleGS1ProductNumberSeparately
          handleGS1LotSeparately
          ignoreGS1Lot
          scanRequired
          scanRequiredWhitelist
        }
      }
    }
  }
`;

export const get = gql`
  query Company($id: String!) {
    company(id: $id) {
      ...Company
    }
  }
  ${company}
`;

export interface GetCompanyVariables {
  id: string;
}

export interface GetCompanyResponse {
  company: Company;
}

export const getAll = gql`
  query Companies($page: Float) {
    companies(page: $page) {
      data {
        ...Company
      }
      page
      pageSize
      batchSize
      hasNext
    }
  }
  ${company}
`;

export interface GetCompaniesVariables {}

export interface GetCompaniesResponse {
  companies: PaginatedCompanies;
}

export class PaginatedCompanies extends forPaginated<Company>() {}

export const create = gql`
  mutation CreateCompany($company: CreateCompanyInput!) {
    createCompany(company: $company) {
      ...Company
    }
  }
  ${company}
`;

export interface CreateCompanyVariables {
  company: {
    name: string;
    settings?: CompanySettings;
  };
}

export interface CreateCompanyResponse {
  createCompany: { id: string };
}

export const update = gql`
  mutation UpdateCompany($company: UpdateCompanyInput!) {
    updateCompany(company: $company) {
      ...Company
    }
  }
  ${company}
`;

export interface UpdateCompanyVariables {
  company: UpdateCompanyInput;
}

export interface UpdateCompanyResponse {
  updateCompany: Company;
}

export const remove = gql`
  mutation DeleteCompany($company: DeleteCompanyInput!) {
    deleteCompany(company: $company) {
      ...Company
    }
  }
  ${company}
`;

export interface DeleteCompanyVariables {
  company: DeleteCompanyInput;
}

export interface DeleteCompanyResponse {
  deleteCompany: Company;
}

const createDemo = gql`
  mutation CreateDemoCompany($parameters: CreateDemoCompanyParameters) {
    createDemoCompany(parameters: $parameters) {
      ...Company
    }
  }
  ${company}
`;

export interface CreateDemoCompanyVariables {
  parameters?: {
    name?: string;
  };
}

export interface CreateDemoCompanyResponse {
  createDemoCompany: Company;
}

export const CompanyQueries = {
  get,
  getAll,
};

export const CompanyMutations = {
  create,
  update,
  remove,
  createDemo,
};

export const CompanyFragments = {
  company,
};
